import React from 'react';
import { PublicLayout, SocialProofBand } from '@/components/public';
import HeroSection from './join/HeroSection';
import IntegrationsStrip from './join/IntegrationsStrip';
import RoiCalculatorSection from './join/RoiCalculatorSection';
import PartnershipProcessSection from './join/PartnershipProcessSection';
import BuiltForSection from './join/BuiltForSection';
import SupportResourcesSection from './join/SupportResourcesSection';
import PricingPreviewSection from './join/PricingPreviewSection';
import FaqSection from './join/FaqSection';
import { BRAND } from '@/config/brand';
import { SOLUTIONS_FOOTER_COLUMN } from '@/config/verticals';

/**
 * / — Public merchant-facing landing page.
 * Composed from reusable public primitives + section components.
 */
const NAV_LINKS = [
  { href: '#roi-calculator', label: 'Savings', testId: 'nav-savings' },
  { href: '#pricing', label: 'Pricing', testId: 'nav-pricing' },
  { href: '/integrations', label: 'Integrations', testId: 'nav-integrations' },
  { href: '#how-partnership-works', label: 'Process', testId: 'nav-process' },
  { href: '#built-for', label: "Who it's for", testId: 'nav-built-for' },
  { href: '#support-resources', label: 'Support', testId: 'nav-support' },
  { href: '#faq', label: 'FAQ', testId: 'nav-faq' },
];

const FOOTER_COLUMNS = [
  {
    title: 'For Restaurants',
    links: [
      { href: '#roi-calculator', label: 'Calculate savings', testId: 'footer-why' },
      { href: '#pricing', label: 'Pricing', testId: 'footer-how' },
      { href: '#faq', label: 'FAQ', testId: 'footer-faq' },
    ],
  },
  SOLUTIONS_FOOTER_COLUMN,
  {
    title: 'Company',
    links: [
      { href: BRAND.website, label: 'Main site', external: true, testId: 'footer-main-site' },
      {
        href: `mailto:${BRAND.merchantsEmail}`,
        label: 'Contact merchants team',
        external: true,
        testId: 'footer-contact',
      },
    ],
  },
  {
    title: 'Resources',
    links: [
      { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'footer-help' },
      { href: '/features', label: 'Features', testId: 'footer-features' },
      { href: '/integrations', label: 'Integrations', testId: 'footer-integrations' },
      { href: '/tools', label: 'Tools', testId: 'footer-tools' },
    ],
  },
];

const FOOTER_LEGAL = [
  { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'footer-tos' },
  { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'footer-privacy' },
];

const NAV_PROPS = {
  links: NAV_LINKS,
  primaryCta: { href: '#lead-form-anchor', label: 'Apply now', testId: 'nav-apply-now' },
};

const FOOTER_PROPS = {
  columns: FOOTER_COLUMNS,
  legal: FOOTER_LEGAL,
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
};

const scrollToForm = (e) => {
  e.preventDefault();
  document.getElementById('lead-form-anchor')?.scrollIntoView({ behavior: 'smooth' });
};

export default function LandingPage() {
  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <HeroSection />
      <SocialProofBand />
      <IntegrationsStrip />
      <RoiCalculatorSection />
      <PricingPreviewSection />
      <PartnershipProcessSection />
      <BuiltForSection />
      <SupportResourcesSection />
      <FaqSection />

      {/* Mobile persistent conversion bar */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-background/90 backdrop-blur-xl border-t border-border p-3"
        data-testid="mobile-sticky-cta-bar"
      >
        <a
          href="#lead-form-anchor"
          onClick={scrollToForm}
          data-testid="mobile-sticky-cta"
          className="flex items-center justify-center w-full h-12 rounded-full bg-primary text-primary-foreground text-base font-semibold"
        >
          Apply now
        </a>
      </div>
      <div className="h-20 md:hidden" aria-hidden />
    </PublicLayout>
  );
}
